import React, { use, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconButton } from '@mui/material';
import { Favorite, Home } from '@mui/icons-material';
import NotificationBell from '../../notifications/NotificationBell';
import { NannieLogo, PicassoTitle } from '../../ui/BrandingComponents';
import UserDropdown from '@/components/ui/UserDropdown';
import ProfileMenu, { DashboardIcon, VideoIcon, UsersIcon, /* ArchiveIcon, */ getProfileMenuSections } from '../../common/ProfileMenu';
import { DeviceContext } from '@/hooks/common/DeviceContext';
import { useClickOutside } from '@/hooks/common/customHooks';
import parsnipNavIcon from '@/assets/icons/brand/parsnip_navigation.svg';

interface AdminHeaderProps {
  currentAdmin: string;
  onLogout: () => Promise<void>;
}

/**
 * AdminHeader - Header component for admin pages
 * 
 * Provides navigation and user controls for admin interface.
 * Includes logo, notifications, and user dropdown with role-based navigation.
 * 
 * Features:
 * - Responsive navigation bar
 * - Logo with refresh functionality
 * - Notification bell for admin alerts
 * - User dropdown with role-based navigation
 * - Consistent styling across admin pages
 * 
 * @param currentAdmin - The current admin user's email
 * @param onLogout - Function to handle user logout
 */
const AdminHeader: React.FC<AdminHeaderProps> = ({
  currentAdmin,
  onLogout
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if currently on favourites page
  const isFavouritesPage = location.pathname === '/favourites';

  /**
   * Handle video notification click - open video modal
   */
  const handleVideoNotificationClick = (videoId: string) => {
    // Dispatch custom event to communicate with VideoGallery
    globalThis.dispatchEvent(new CustomEvent('openAdminVideoModal', { 
      detail: { videoId } 
    }));
  };

  /**
   * Handle favorites click - toggle between favorites page and admin dashboard
   */
  const handleFavoritesClick = () => {
    if (isFavouritesPage) {
      // If already on favourites page, go to admin dashboard
      navigate('/dashboard');
    } else {
      // Otherwise, go to favourites page
      navigate('/favourites');
    }
  };

  /**
   * Handles page refresh when logo is clicked
   */
  const handleRefresh = () => {
    globalThis.location.reload();
  };

  // Define dropdown navigation items with active state tracking
  const dropdownItems = [
    {
      label: 'Content Creation',
      icon: <DashboardIcon />,
      onClick: () => navigate('/dashboard'),
      active: location.pathname === '/dashboard'
    },
    {
      label: 'Admin Dashboard', 
      icon: <UsersIcon />,
      onClick: () => navigate('/admin'),
      active: location.pathname === '/admin'
    },
    {
      label: 'Content Review',
      icon: <VideoIcon />,
      onClick: () => navigate('/content-review'),
      active: location.pathname === '/content-review'
    },
    {
      label: 'Parsnip',
      icon: <img src={parsnipNavIcon} alt="" className="w-5 h-5" style={{ filter: 'contrast(1.5)' }} />,
      onClick: () => navigate('/parsnip'),
      active: location.pathname === '/parsnip'
    },
    // Commented out Archives dropdown item
    // {
    //   label: 'Archives',
    //   icon: <ArchiveIcon />,
    //   onClick: () => navigate('/archives'),
    //   active: location.pathname === '/archives'
    // },
    {
      label: 'Test Environment',
      icon: <DashboardIcon />,
      onClick: () => globalThis.open('https://testingpicasso.netlify.app/login', '_blank', 'noopener,noreferrer'),
      active: false // External link, never active
    }
  ];

  const { isMobile } = use(DeviceContext) ?? { isTablet: false, isMobile: false };
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useClickOutside<HTMLDivElement>(() => setShowDropdown(false));

  return (
    <header className="bg-white">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3">
          {/* Logo Section */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              {isMobile ? <PicassoTitle /> : <NannieLogo onClick={handleRefresh} />}
            </div>
          </div>

          {/* Actions Section */}
          <div className="flex items-center">
            {/* Favorites/Home Icon */}
            <IconButton
              onClick={handleFavoritesClick}
              color="inherit"
              size="large"
              aria-label={isFavouritesPage ? "Go to dashboard" : "View favorites"}
              sx={{ marginRight: '-4px' }}
            >
              {isFavouritesPage ? (
                <Home sx={{ color: 'black' }} />
              ) : (
                <Favorite sx={{ color: 'black' }} />
              )}
            </IconButton>

            {/* Notification Bell */}
            <NotificationBell 
              userEmail={currentAdmin}
              isAdmin={true}
              onVideoClick={handleVideoNotificationClick}
            />
            
            {/* Mobile: Profile Image Dropdown, Desktop: UserDropdown */}
            {isMobile ? (
              <div className="relative" ref={dropdownRef}>
                <button 
                  type="button"
                  id="profile-menu-button"
                  className="w-[32px] h-[32px] rounded-full bg-white p-0"
                  onClick={() => setShowDropdown(!showDropdown)}
                  aria-label="Open profile menu"
                  aria-expanded={showDropdown}
                  aria-haspopup="menu"
                >
                  <img 
                    src="https://storage.googleapis.com/nannie_profile_images/Profile_dog.webp" 
                    alt="Profile" 
                    className="w-full h-full object-cover rounded-full"
                  />
                </button>
                <ProfileMenu
                  showDropdown={showDropdown}
                  sections={getProfileMenuSections(true, location.pathname)}
                  onLogout={async () => {
                    setShowDropdown(false);
                    await onLogout();
                  }}
                />
              </div>
            ) : (
              <UserDropdown
                userEmail={currentAdmin}
                userRole="Admin"
                items={dropdownItems}
                onLogout={onLogout}
              />
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
